/**
 * Quiz questions mapping to PCA dimensions
 * Each question maps to one or more dimensions with weights
 */

export interface QuizQuestion {
  id: string;
  text: string;
  type: 'multiple-choice' | 'likert';
  options: {
    label: string;
    value: number; // -1 to 1, where -1 = low end, 1 = high end
    dimensionWeights: Record<number, number>; // dimension index (1-20) -> weight (-1 to 1)
  }[];
}

export const QUIZ_QUESTIONS: QuizQuestion[] = [
  {
    id: 'q1',
    text: 'When prioritizing features, you primarily rely on:',
    type: 'multiple-choice',
    options: [
      {
        label: 'Data analysis and user metrics',
        value: -1,
        dimensionWeights: { 2: -0.8, 12: -0.6 } // Data Strategist, Data-Driven Decider
      },
      {
        label: 'User feedback and qualitative insights',
        value: 1,
        dimensionWeights: { 2: 0.8, 12: 0.6 } // Product Craftsperson, Collaborative Builder
      },
      {
        label: 'A balanced mix of both',
        value: 0,
        dimensionWeights: {}
      }
    ]
  },
  {
    id: 'q2',
    text: 'Your ideal product work involves:',
    type: 'multiple-choice',
    options: [
      {
        label: 'Building systems and architecture',
        value: -1,
        dimensionWeights: { 3: -0.7, 5: -0.6 } // Product Architect, Technical Builder
      },
      {
        label: 'Driving growth and user acquisition',
        value: 1,
        dimensionWeights: { 3: 0.7, 9: 0.6 } // Growth Driver, Marketing Driver
      },
      {
        label: 'Both equally',
        value: 0,
        dimensionWeights: {}
      }
    ]
  },
  {
    id: 'q3',
    text: 'When making product decisions, you prefer:',
    type: 'multiple-choice',
    options: [
      {
        label: 'Collaborating with the team to build consensus',
        value: 1,
        dimensionWeights: { 1: 0.7, 12: 0.6, 19: 0.5 } // Product Collaborator, Collaborative Builder, Team Collaborator
      },
      {
        label: 'Making data-driven decisions independently',
        value: -1,
        dimensionWeights: { 12: -0.7, 5: -0.5 } // Data-Driven Decider, Business Decision-Maker
      },
      {
        label: 'Depends on the situation',
        value: 0,
        dimensionWeights: {}
      }
    ]
  },
  {
    id: 'q4',
    text: 'Your focus is more on:',
    type: 'multiple-choice',
    options: [
      {
        label: 'Optimizing existing operations and processes',
        value: -1,
        dimensionWeights: { 7: -0.8, 9: -0.6 } // Operational Optimizer, Retention Optimizer
      },
      {
        label: 'Creating new innovations and experiments',
        value: 1,
        dimensionWeights: { 7: 0.8, 19: 0.5 } // Innovation Catalyst, Brand Innovator
      },
      {
        label: 'Both',
        value: 0,
        dimensionWeights: {}
      }
    ]
  },
  {
    id: 'q5',
    text: 'You spend more time:',
    type: 'multiple-choice',
    options: [
      {
        label: 'On personal development and self-improvement',
        value: -1,
        dimensionWeights: { 1: -0.8 } // Self-Developer
      },
      {
        label: 'On product work and team collaboration',
        value: 1,
        dimensionWeights: { 1: 0.8 } // Product Collaborator
      },
      {
        label: 'Balanced between both',
        value: 0,
        dimensionWeights: {}
      }
    ]
  },
  {
    id: 'q6',
    text: 'When building a product, you prioritize:',
    type: 'multiple-choice',
    options: [
      {
        label: 'Technical excellence and architecture',
        value: -1,
        dimensionWeights: { 5: -0.7, 20: -0.6 } // Technical Builder, Technical Developer
      },
      {
        label: 'Business impact and user value',
        value: 1,
        dimensionWeights: { 5: 0.7, 2: 0.5 } // Business Decision-Maker, Product Craftsperson
      },
      {
        label: 'Both equally',
        value: 0,
        dimensionWeights: {}
      }
    ]
  },
  {
    id: 'q7',
    text: 'Your approach to product strategy:',
    type: 'multiple-choice',
    options: [
      {
        label: 'Founder-like: building vision and narrative',
        value: -1,
        dimensionWeights: { 4: -0.7, 8: -0.6 } // Founder-Minded, Narrative Founder
      },
      {
        label: 'Corporate: structured planning and execution',
        value: 1,
        dimensionWeights: { 4: 0.7, 10: 0.5 } // Corporate Strategist, Productivity Master
      },
      {
        label: 'Mix of both',
        value: 0,
        dimensionWeights: {}
      }
    ]
  },
  {
    id: 'q8',
    text: 'You focus more on:',
    type: 'multiple-choice',
    options: [
      {
        label: 'Retaining and optimizing existing users',
        value: -1,
        dimensionWeights: { 9: -0.8 } // Retention Optimizer
      },
      {
        label: 'Acquiring new users through marketing',
        value: 1,
        dimensionWeights: { 9: 0.8 } // Marketing Driver
      },
      {
        label: 'Both equally',
        value: 0,
        dimensionWeights: {}
      }
    ]
  },
  {
    id: 'q9',
    text: 'Your leadership style:',
    type: 'multiple-choice',
    options: [
      {
        label: 'Productivity-focused: systems and execution',
        value: -1,
        dimensionWeights: { 10: -0.7, 16: -0.5 } // Productivity Master, Ship Master
      },
      {
        label: 'Visionary: inspiring and setting direction',
        value: 1,
        dimensionWeights: { 10: 0.7, 17: 0.6, 20: 0.5 } // Visionary Leader, Thought Leader, Inspirational Influencer
      },
      {
        label: 'Both',
        value: 0,
        dimensionWeights: {}
      }
    ]
  },
  {
    id: 'q10',
    text: 'When analyzing competition, you:',
    type: 'multiple-choice',
    options: [
      {
        label: 'Focus on external market and competitors',
        value: -1,
        dimensionWeights: { 11: -0.8 } // Market Competitor
      },
      {
        label: 'Focus on internal metrics and user behavior',
        value: 1,
        dimensionWeights: { 11: 0.8 } // Internal Analyst
      },
      {
        label: 'Both',
        value: 0,
        dimensionWeights: {}
      }
    ]
  },
  {
    id: 'q11',
    text: 'Your role involves more:',
    type: 'multiple-choice',
    options: [
      {
        label: 'Organizing and leading teams',
        value: -1,
        dimensionWeights: { 13: -0.7, 14: -0.5 } // Organizational Leader, Team Orchestrator
      },
      {
        label: 'Building community and external relationships',
        value: 1,
        dimensionWeights: { 13: 0.7 } // Community Builder
      },
      {
        label: 'Both',
        value: 0,
        dimensionWeights: {}
      }
    ]
  },
  {
    id: 'q12',
    text: 'You spend more time:',
    type: 'multiple-choice',
    options: [
      {
        label: 'Executing projects and shipping features',
        value: -1,
        dimensionWeights: { 15: -0.7, 16: -0.6, 17: -0.5 } // Project Executor, Ship Master, Team Executor
      },
      {
        label: 'Creating content and thought leadership',
        value: 1,
        dimensionWeights: { 15: 0.7, 17: 0.6 } // Content Creator, Thought Leader
      },
      {
        label: 'Both',
        value: 0,
        dimensionWeights: {}
      }
    ]
  }
];

/**
 * Calculate user's dimension scores from quiz answers
 * Returns array of 20 scores (one per dimension)
 */
export function calculateDimensionScores(answers: Record<string, number>): number[] {
  const scores = new Array(20).fill(0);
  
  QUIZ_QUESTIONS.forEach(question => {
    const answerValue = answers[question.id];
    if (answerValue === undefined) return;
    
    const selectedOption = question.options.find(opt => opt.value === answerValue);
    if (!selectedOption) return;
    
    // Apply weights to dimensions
    Object.entries(selectedOption.dimensionWeights).forEach(([dimStr, weight]) => {
      const dimIndex = parseInt(dimStr) - 1; // Convert 1-20 to 0-19
      if (dimIndex >= 0 && dimIndex < 20) {
        scores[dimIndex] += weight;
      }
    });
  });
  
  // Normalize scores to roughly -1 to 1 range
  const maxAbs = Math.max(...scores.map(Math.abs), 1);
  return scores.map(s => s / maxAbs);
}
